import React, { useState, useMemo, useEffect } from 'react';
import { Product, Currency, DocumentCategory, SoftwareFormat } from '../types';
import { ProductCard } from './ProductCard';
import { ProductSkeletonGrid } from './ProductSkeleton';
import { SearchAutocomplete } from './SearchAutocomplete';
import { performSmartSearch } from '../lib/searchEngine';
import { isProductPublished } from '../lib/statsService';
import {
  Search,
  Filter,
  X,
  SlidersHorizontal,
  Sliders,
  Check,
  RotateCcw,
  Sparkles,
  Layers,
  Globe,
  FileCode2,
  DollarSign,
  Star,
  CheckCircle2,
  ArrowUpDown,
  Grid,
  List,
  AlertCircle,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

interface SearchResultsPageProps {
  searchQuery: string;
  setSearchQuery: (q: string) => void;
  products: Product[];
  currency: Currency;
  darkMode: boolean;
  onQuickView: (product: Product) => void;
  onAddToCart: (product: Product) => void;
  onQuickBuy?: (product: Product, format?: any) => void;
  onToggleWishlist: (product: Product) => void;
  cartItems: any[];
  wishlistItems: any[];
  onOpenAI: () => void;
  isProductsLoading?: boolean;
  hasFirestoreLoaded?: boolean;
}

export const SearchResultsPage: React.FC<SearchResultsPageProps> = ({
  searchQuery,
  setSearchQuery,
  products,
  currency,
  darkMode,
  onQuickView,
  onQuickBuy,
  onAddToCart,
  onToggleWishlist,
  cartItems,
  wishlistItems,
  onOpenAI,
  isProductsLoading = false,
  hasFirestoreLoaded = true,
}) => {
  // Filter States
  const [selectedCategory, setSelectedCategory] = useState<DocumentCategory | 'All'>('All');
  const [selectedCountry, setSelectedCountry] = useState<string>('All');
  const [selectedFormat, setSelectedFormat] = useState<SoftwareFormat | 'All'>('All');
  const [maxPrice, setMaxPrice] = useState<number>(100);
  const [minRating, setMinRating] = useState<number>(0);
  const [sortBy, setSortBy] = useState<'relevance' | 'latest' | 'popular' | 'price-low' | 'price-high' | 'rating'>('relevance');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  // Pagination & Display States
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(12);

  // Extract list of all available countries from dataset
  const availableCountries = useMemo(() => {
    const set = new Set<string>();
    products.forEach(p => {
      if (p.country) set.add(p.country);
    });
    return Array.from(set).sort();
  }, [products]);

  // Extract list of all available categories
  const availableCategories: (DocumentCategory | 'All')[] = [
    'All',
    'Passport',
    'ID Card',
    'Driving License',
    'Certificate',
    'Utility Bill',
    'Bank Statement',
    'Visa Documents',
    'Resume',
    'Health ID',
    'Templates',
    'Others'
  ];

  // Calculate category product counts based on active products
  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = { All: products.length };
    products.forEach(p => {
      const cat = p.category || 'Others';
      counts[cat] = (counts[cat] || 0) + 1;
    });
    return counts;
  }, [products]);

  // Extract formats
  const availableFormats: (SoftwareFormat | 'All')[] = ['All', 'PSD', 'AI', 'CDR'];

  // Perform Smart Search Engine Execution
  const searchResultResponse = useMemo(() => {
    const validProducts = (products || []).filter(p => p && isProductPublished(p));
    return performSmartSearch(validProducts, searchQuery, {
      category: selectedCategory,
      country: selectedCountry,
      format: selectedFormat,
      maxPrice: maxPrice < 100 ? maxPrice : undefined,
      minRating: minRating > 0 ? minRating : undefined,
      sortBy: sortBy,
    });
  }, [products, searchQuery, selectedCategory, selectedCountry, selectedFormat, maxPrice, minRating, sortBy]);

  const results = searchResultResponse.results;
  const executionTimeMs = searchResultResponse.executionTimeMs;
  const totalMatches = searchResultResponse.totalMatches;

  // Reset page to 1 whenever search query, filters or sort change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedCategory, selectedCountry, selectedFormat, maxPrice, minRating, sortBy, itemsPerPage]);

  // Paginated Results
  const totalPages = Math.max(1, Math.ceil(results.length / itemsPerPage));
  const paginatedResults = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return results.slice(start, start + itemsPerPage);
  }, [results, currentPage, itemsPerPage]);

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 180, behavior: 'smooth' });
  };

  // Active filters count
  const activeFiltersCount =
    (selectedCategory !== 'All' ? 1 : 0) +
    (selectedCountry !== 'All' ? 1 : 0) +
    (selectedFormat !== 'All' ? 1 : 0) +
    (maxPrice < 100 ? 1 : 0) +
    (minRating > 0 ? 1 : 0);

  const handleResetFilters = () => {
    setSelectedCategory('All');
    setSelectedCountry('All');
    setSelectedFormat('All');
    setMaxPrice(100);
    setMinRating(0);
    setSortBy('relevance');
  };

  return (
    <div className={`min-h-screen pt-4 pb-20 transition-colors duration-300 ${darkMode ? 'bg-slate-950 text-white' : 'bg-slate-50 text-slate-900'}`}>
      
      {/* 1. TOP HERO SEARCH HEADER */}
      <div className={`border-b  ${darkMode ? 'bg-slate-900/60 border-slate-800' : 'bg-white/80 border-slate-200'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
            <div>
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-cyan-400 mb-1">
                <Search className="w-4 h-4" />
                <span>Enterprise Marketplace Search</span>
              </div>

              <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
                {searchQuery ? (
                  <>Results for <span className="text-gradient">"{searchQuery}"</span></>
                ) : (
                  <>Browse Marketplace Catalog</>
                )}
              </h1>

              <p className="text-xs sm:text-sm text-slate-400 mt-1 flex items-center gap-2">
                <span>Showing <strong className="text-cyan-400 font-bold">{totalMatches}</strong> products</span>
                <span>•</span>
                <span>Search execution: <strong className="text-slate-300 font-mono">{executionTimeMs}s</strong></span>
              </p>
            </div>

            {/* Embedded Live Search Refinement Input */}
            <div className="w-full lg:w-[480px]">
              <SearchAutocomplete
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                onSelectProduct={onQuickView}
                onPerformSearch={(q) => setSearchQuery(q)}
                products={products}
                darkMode={darkMode}
                size="md"
                placeholder="Refine search (e.g., USA Passport, Utility Bill PSD)..."
              />
            </div>
          </div>

          {/* Active Filter Pills Bar */}
          {activeFiltersCount > 0 && (
            <div className="flex flex-wrap items-center gap-2 pt-6 mt-6 border-t border-white/10">
              <span className="text-xs font-bold text-slate-400 mr-1 flex items-center gap-1">
                <Filter className="w-3.5 h-3.5 text-cyan-400" />
                Active Filters ({activeFiltersCount}):
              </span>

              {selectedCategory !== 'All' && (
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-xl bg-blue-500/20 border border-blue-500/40 text-blue-300 text-xs font-bold">
                  Category: {selectedCategory}
                  <button onClick={() => setSelectedCategory('All')} className="hover:text-white"><X className="w-3 h-3" /></button>
                </span>
              )}

              {selectedCountry !== 'All' && (
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-xl bg-purple-500/20 border border-purple-500/40 text-purple-300 text-xs font-bold">
                  Country: {selectedCountry}
                  <button onClick={() => setSelectedCountry('All')} className="hover:text-white"><X className="w-3 h-3" /></button>
                </span>
              )}

              {selectedFormat !== 'All' && (
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-xl bg-cyan-500/20 border border-cyan-500/40 text-cyan-300 text-xs font-bold">
                  Format: {selectedFormat}
                  <button onClick={() => setSelectedFormat('All')} className="hover:text-white"><X className="w-3 h-3" /></button>
                </span>
              )}

              {maxPrice < 100 && (
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-xl bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 text-xs font-bold">
                  Max Price: ${maxPrice}
                  <button onClick={() => setMaxPrice(100)} className="hover:text-white"><X className="w-3 h-3" /></button>
                </span>
              )}

              {minRating > 0 && (
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-xl bg-amber-500/20 border border-amber-500/40 text-amber-300 text-xs font-bold">
                  Rating: {minRating}+ Stars
                  <button onClick={() => setMinRating(0)} className="hover:text-white"><X className="w-3 h-3" /></button>
                </span>
              )}

              <button
                onClick={handleResetFilters}
                className="text-xs font-bold text-rose-400 hover:text-rose-300 flex items-center gap-1 ml-2 transition-colors"
              >
                <RotateCcw className="w-3 h-3" />
                Clear All
              </button>
            </div>
          )}

        </div>
      </div>

      {/* 2. MAIN SEARCH BODY LAYOUT (Sidebar Filters + Results Grid) */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        
        {/* Controls Bar (Mobile Filter Toggle, Sorting, View Mode) */}
        <div className="flex items-center justify-between gap-4 mb-6">
          
          <button
            onClick={() => setMobileFiltersOpen(true)}
            className="lg:hidden flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-900 border border-slate-800 text-xs font-bold text-slate-200"
          >
            <SlidersHorizontal className="w-4 h-4 text-cyan-400" />
            <span>Filters {activeFiltersCount > 0 && `(${activeFiltersCount})`}</span>
          </button>

          <div className="hidden lg:flex items-center gap-2 text-xs font-bold text-slate-400">
            <Sliders className="w-4 h-4 text-cyan-400" />
            <span>Refine Search Results</span>
          </div>

          <div className="flex items-center gap-3 ml-auto">
            
            {/* Sort Dropdown */}
            <div className="flex items-center gap-2">
              <span className="hidden sm:inline text-xs font-bold text-slate-400">Sort By:</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as any)}
                className={`px-3 py-2 rounded-xl text-xs font-bold border focus:outline-none ${
                  darkMode
                    ? 'bg-slate-900 border-slate-700 text-white'
                    : 'bg-white border-slate-300 text-slate-800'
                }`}
              >
                <option value="relevance">Relevance Score</option>
                <option value="latest">Newest Releases</option>
                <option value="popular">Best Selling / Popular</option>
                <option value="rating">Highest Rated</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
              </select>
            </div>

            {/* View Mode Toggle */}
            <div className={`hidden sm:flex items-center p-1 rounded-xl border ${darkMode ? 'bg-slate-900 border-slate-800' : 'bg-slate-100 border-slate-300'}`}>
              <button
                onClick={() => setViewMode('grid')}
                className={`p-1.5 rounded-lg text-xs transition-colors ${viewMode === 'grid' ? 'bg-cyan-500 text-white font-bold shadow' : 'text-slate-400 hover:text-white'}`}
                title="Grid View"
              >
                <Grid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-1.5 rounded-lg text-xs transition-colors ${viewMode === 'list' ? 'bg-cyan-500 text-white font-bold shadow' : 'text-slate-400 hover:text-white'}`}
                title="List View"
              >
                <List className="w-4 h-4" />
              </button>
            </div>

          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          
          {/* DESKTOP SIDEBAR FILTERS */}
          <aside className="hidden lg:block space-y-6">
            <div className={`p-5 rounded-2xl border ${darkMode ? 'bg-slate-900/95 border-slate-800' : 'bg-white border-slate-200'} space-y-6 shadow-xl sticky top-24`}>
              
              <div className="flex items-center justify-between pb-4 border-b border-white/10">
                <h3 className="text-sm font-extrabold uppercase tracking-wider flex items-center gap-2">
                  <Filter className="w-4 h-4 text-cyan-400" />
                  <span>Search Filters</span>
                </h3>
                {activeFiltersCount > 0 && (
                  <button
                    onClick={handleResetFilters}
                    className="text-xs font-bold text-rose-400 hover:text-rose-300 transition-colors"
                  >
                    Reset
                  </button>
                )}
              </div>

              {/* 1. Category Filter */}
              <div>
                <label className="text-xs font-bold text-slate-400 uppercase tracking-wider block mb-2 flex items-center justify-between">
                  <span className="flex items-center gap-1.5">
                    <Layers className="w-3.5 h-3.5 text-purple-400" />
                    Document Category
                  </span>
                  {selectedCategory !== 'All' && (
                    <button
                      type="button"
                      onClick={() => setSelectedCategory('All')}
                      className="text-[10px] text-cyan-400 hover:underline lowercase font-mono"
                    >
                      clear
                    </button>
                  )}
                </label>
                <div className="space-y-1 max-h-52 overflow-y-auto scrollbar-thin pr-1">
                  {availableCategories.map(cat => {
                    const count = categoryCounts[cat] || 0;
                    const isSel = selectedCategory === cat;
                    return (
                      <button
                        key={cat}
                        type="button"
                        onClick={() => setSelectedCategory(cat)}
                        className={`w-full flex items-center justify-between px-3 py-1.5 rounded-xl text-xs font-semibold transition-colors cursor-pointer ${
                          isSel
                            ? 'bg-blue-600 text-white font-bold shadow-md'
                            : darkMode
                            ? 'text-slate-300 hover:bg-slate-800/80'
                            : 'text-slate-700 hover:bg-slate-100'
                        }`}
                      >
                        <span className="truncate pr-2">{cat}</span>
                        <span className={`text-[10px] font-mono font-bold px-1.5 py-0.2 rounded-full shrink-0 ${
                          isSel ? 'bg-white/20 text-white' : 'bg-slate-800/80 text-cyan-400 border border-slate-700/60'
                        }`}>
                          {count}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* 2. Country Filter */}
              <div>
                <label className="text-xs font-bold text-slate-400 uppercase tracking-wider block mb-2 flex items-center gap-1.5">
                  <Globe className="w-3.5 h-3.5 text-cyan-400" />
                  Country / Region
                </label>
                <select
                  value={selectedCountry}
                  onChange={(e) => setSelectedCountry(e.target.value)}
                  className={`w-full px-3 py-2 rounded-xl text-xs font-semibold border focus:outline-none ${
                    darkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-slate-50 border-slate-300 text-slate-900'
                  }`}
                >
                  <option value="All">All Countries / Global</option>
                  {availableCountries.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>

              {/* 3. Software Format */}
              <div>
                <label className="text-xs font-bold text-slate-400 uppercase tracking-wider block mb-2 flex items-center gap-1.5">
                  <FileCode2 className="w-3.5 h-3.5 text-emerald-400" />
                  Software Format
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {availableFormats.map(fmt => (
                    <button
                      key={fmt}
                      type="button"
                      onClick={() => setSelectedFormat(fmt)}
                      className={`px-3 py-1 rounded-xl text-xs font-bold border transition-colors ${
                        selectedFormat === fmt
                          ? 'bg-cyan-500 text-white border-cyan-400 shadow-md'
                          : darkMode
                          ? 'bg-slate-800/60 border-slate-700 text-slate-300 hover:bg-slate-700'
                          : 'bg-slate-100 border-slate-200 text-slate-700 hover:bg-slate-200'
                      }`}
                    >
                      {fmt}
                    </button>
                  ))}
                </div>
              </div>

              {/* 4. Price Slider */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
                    <DollarSign className="w-3.5 h-3.5 text-amber-400" />
                    Max Price
                  </label>
                  <span className="text-xs font-extrabold text-cyan-400">${maxPrice}</span>
                </div>
                <input
                  type="range"
                  min="5"
                  max="100"
                  step="5"
                  value={maxPrice}
                  onChange={(e) => setMaxPrice(Number(e.target.value))}
                  className="w-full accent-cyan-400 cursor-pointer"
                />
                <div className="flex justify-between text-[10px] text-slate-500 font-bold mt-1">
                  <span>$5</span>
                  <span>$50</span>
                  <span>$100+</span>
                </div>
              </div>

              {/* 5. Rating Filter */}
              <div>
                <label className="text-xs font-bold text-slate-400 uppercase tracking-wider block mb-2 flex items-center gap-1.5">
                  <Star className="w-3.5 h-3.5 text-yellow-400" />
                  Minimum Rating
                </label>
                <div className="flex items-center gap-1">
                  {[0, 4, 4.5, 4.8].map(r => (
                    <button
                      key={r}
                      type="button"
                      onClick={() => setMinRating(r)}
                      className={`px-2.5 py-1 rounded-lg text-xs font-bold border transition-colors ${
                        minRating === r
                          ? 'bg-amber-500 text-white border-amber-400 shadow-md'
                          : darkMode
                          ? 'bg-slate-800 border-slate-700 text-slate-300'
                          : 'bg-slate-100 border-slate-200 text-slate-700'
                      }`}
                    >
                      {r === 0 ? 'Any' : `${r}★`}
                    </button>
                  ))}
                </div>
              </div>

            </div>
          </aside>

          {/* MAIN RESULTS GRID CONTAINER */}
          <main className="lg:col-span-3">
            {isProductsLoading ? (
              <ProductSkeletonGrid count={6} darkMode={darkMode} />
            ) : results.length > 0 ? (
              <div className="space-y-8">
                {/* Product Grid / List */}
                <div className={viewMode === 'grid' ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6' : 'space-y-4'}>
                  {paginatedResults.map((product, idx) => (
                    <ProductCard
                      key={`${product.id}-${idx}`}
                      product={product}
                      currency={currency}
                      darkMode={darkMode}
                      onQuickView={onQuickView}
                      onQuickBuy={onQuickBuy}
                      onAddToCart={onAddToCart}
                      onToggleWishlist={onToggleWishlist}
                      isInWishlist={wishlistItems.some((item: any) => item.id === product.id)}
                    />
                  ))}
                </div>

                {/* SEARCH RESULTS PAGINATION BAR */}
                {totalPages > 1 && (
                  <div className={`p-4 rounded-2xl border flex flex-col sm:flex-row items-center justify-between gap-4 text-xs font-semibold ${
                    darkMode ? 'bg-slate-900/95 border-slate-800' : 'bg-slate-100 border-slate-200'
                  }`}>
                    <div className="flex items-center gap-3">
                      <span className="text-slate-400">
                        Showing <strong className="text-white">{(currentPage - 1) * itemsPerPage + 1}</strong>–<strong className="text-white">{Math.min(currentPage * itemsPerPage, results.length)}</strong> of <strong className="text-cyan-400">{results.length}</strong> products
                      </span>

                      {/* Items per page selector */}
                      <div className="flex items-center gap-1.5 pl-3 border-l border-slate-700/60">
                        <span className="text-slate-400 text-[11px]">Per page:</span>
                        <select
                          value={itemsPerPage}
                          onChange={(e) => setItemsPerPage(Number(e.target.value))}
                          className={`px-2 py-1 rounded-lg text-xs font-bold border ${
                            darkMode ? 'bg-slate-950 border-slate-700 text-white' : 'bg-white border-slate-300 text-slate-900'
                          } cursor-pointer`}
                        >
                          <option value={12}>12</option>
                          <option value={24}>24</option>
                          <option value={36}>36</option>
                        </select>
                      </div>
                    </div>

                    {/* Pagination buttons */}
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        disabled={currentPage === 1}
                        onClick={() => handlePageChange(currentPage - 1)}
                        className={`px-3 py-1.5 rounded-xl border font-bold flex items-center gap-1 transition-all ${
                          currentPage === 1
                            ? 'opacity-40 border-slate-800 text-slate-600 cursor-not-allowed'
                            : 'bg-slate-950 border-slate-700 text-white hover:bg-slate-800 cursor-pointer'
                        }`}
                      >
                        <ChevronLeft className="w-3.5 h-3.5" />
                        <span>Prev</span>
                      </button>

                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
                        <button
                          key={pageNum}
                          type="button"
                          onClick={() => handlePageChange(pageNum)}
                          className={`w-8 h-8 rounded-xl font-extrabold text-xs transition-all cursor-pointer ${
                            currentPage === pageNum
                              ? 'bg-cyan-500 text-slate-950 shadow-md font-black scale-105'
                              : 'bg-slate-950 border border-slate-800 text-slate-300 hover:bg-slate-800'
                          }`}
                        >
                          {pageNum}
                        </button>
                      ))}

                      <button
                        type="button"
                        disabled={currentPage === totalPages}
                        onClick={() => handlePageChange(currentPage + 1)}
                        className={`px-3 py-1.5 rounded-xl border font-bold flex items-center gap-1 transition-all ${
                          currentPage === totalPages
                            ? 'opacity-40 border-slate-800 text-slate-600 cursor-not-allowed'
                            : 'bg-slate-950 border-slate-700 text-white hover:bg-slate-800 cursor-pointer'
                        }`}
                      >
                        <span>Next</span>
                        <ChevronRight className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              /* NO RESULTS FOUND FALLBACK */
              <div className={`p-8 sm:p-12 rounded-3xl border text-center ${darkMode ? 'bg-slate-900/95 border-slate-800' : 'bg-white border-slate-200'} shadow-2xl`}>
                <div className="w-16 h-16 mx-auto rounded-2xl bg-rose-500/10 border border-rose-500/20 text-rose-400 flex items-center justify-center mb-4">
                  <AlertCircle className="w-8 h-8" />
                </div>

                <h3 className="text-xl font-extrabold tracking-tight mb-2">
                  No exact matches found for "{searchQuery}"
                </h3>

                <p className="text-sm text-slate-400 max-w-md mx-auto mb-6">
                  We couldn't find any templates matching your exact search criteria. Try removing some filters or searching for related keywords.
                </p>

                <div className="flex flex-wrap items-center justify-center gap-3 mb-8">
                  <button
                    onClick={handleResetFilters}
                    className="px-4 py-2 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold text-xs shadow-md"
                  >
                    Reset All Search Filters
                  </button>
                  <button
                    onClick={() => setSearchQuery('Passport')}
                    className="px-4 py-2 rounded-xl bg-slate-800 text-slate-200 font-bold text-xs border border-slate-700 hover:bg-slate-700"
                  >
                    Search "Passport"
                  </button>
                  <button
                    onClick={() => setSearchQuery('Bank Statement')}
                    className="px-4 py-2 rounded-xl bg-slate-800 text-slate-200 font-bold text-xs border border-slate-700 hover:bg-slate-700"
                  >
                    Search "Bank Statement"
                  </button>
                </div>

                {/* Closest Matches / Popular Showcase */}
                <div className="text-left border-t border-white/10 pt-8 mt-4">
                  <h4 className="text-sm font-extrabold uppercase tracking-wider text-cyan-400 mb-4 flex items-center gap-2">
                    <Sparkles className="w-4 h-4" />
                    <span>Popular Template Alternatives</span>
                  </h4>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {products.slice(0, 3).map((prod, idx) => (
                      <ProductCard
                        key={`${prod.id}-${idx}`}
                        product={prod}
                        currency={currency}
                        darkMode={darkMode}
                        onQuickView={onQuickView}
                      onQuickBuy={onQuickBuy}
                        onAddToCart={onAddToCart}
                        onToggleWishlist={onToggleWishlist}
                        isInWishlist={wishlistItems.some((item: any) => item.id === prod.id)}
                      />
                    ))}
                  </div>
                </div>

              </div>
            )}
          </main>

        </div>
      </div>

      {/* MOBILE SLIDE-OVER FILTERS DRAWER */}
      {mobileFiltersOpen && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/70  lg:hidden animate-in fade-in">
          <div className={`w-full max-w-xs h-full p-6 overflow-y-auto space-y-6 ${darkMode ? 'bg-slate-900 text-white' : 'bg-white text-slate-900'} shadow-2xl`}>
            
            <div className="flex items-center justify-between pb-4 border-b border-white/10">
              <h3 className="text-base font-extrabold flex items-center gap-2">
                <Filter className="w-4 h-4 text-cyan-400" />
                <span>Search Filters</span>
              </h3>
              <button onClick={() => setMobileFiltersOpen(false)} className="p-2 text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Mobile Category Filter */}
            <div>
              <label className="text-xs font-bold text-slate-400 uppercase tracking-wider block mb-2">Category</label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value as any)}
                className={`w-full px-3 py-2 rounded-xl text-xs font-semibold border ${darkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-slate-100 border-slate-300'}`}
              >
                {availableCategories.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>

            {/* Mobile Country Filter */}
            <div>
              <label className="text-xs font-bold text-slate-400 uppercase tracking-wider block mb-2">Country</label>
              <select
                value={selectedCountry}
                onChange={(e) => setSelectedCountry(e.target.value)}
                className={`w-full px-3 py-2 rounded-xl text-xs font-semibold border ${darkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-slate-100 border-slate-300'}`}
              >
                <option value="All">All Countries</option>
                {availableCountries.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>

            {/* Mobile Format Filter */}
            <div>
              <label className="text-xs font-bold text-slate-400 uppercase tracking-wider block mb-2">Format</label>
              <div className="flex flex-wrap gap-1.5">
                {availableFormats.map(f => (
                  <button
                    key={f}
                    onClick={() => setSelectedFormat(f)}
                    className={`px-3 py-1 rounded-xl text-xs font-bold border ${selectedFormat === f ? 'bg-cyan-500 text-white' : 'bg-slate-800 text-slate-300'}`}
                  >
                    {f}
                  </button>
                ))}
              </div>
            </div>

            <div className="pt-4 space-y-2">
              <button
                onClick={() => setMobileFiltersOpen(false)}
                className="w-full py-3 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-bold text-xs shadow-md"
              >
                Apply Filters ({results.length} Results)
              </button>
              <button
                onClick={() => {
                  handleResetFilters();
                  setMobileFiltersOpen(false);
                }}
                className="w-full py-2 rounded-xl bg-slate-800 text-slate-300 font-bold text-xs"
              >
                Reset Filters
              </button>
            </div>

          </div>
        </div>
      )}

    </div>
  );
};
